import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { LifeBuoy, Send, Loader2, LogIn, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = {
  open: { label: 'Open', color: '#C86F53' },
  in_progress: { label: 'In progress', color: '#D99E4A' },
  resolved: { label: 'Resolved', color: '#7A9A78' },
};

export default function SupportTicketForm() {
  const { user, login } = useAuth();
  const [data, setData] = useState({ tickets: [], active: 0, max_active: 2 });
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const refresh = () => api.get('/me/support-tickets').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    if (user) refresh();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error('Please add a subject and message'); return; }
    setSending(true);
    try {
      await api.post('/support-tickets', { subject: subject.trim(), message: message.trim() });
      toast.success('Support ticket submitted');
      setSubject(''); setMessage('');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit ticket');
    } finally {
      setSending(false);
    }
  };

  const atLimit = data.active >= data.max_active;

  return (
    <div className="mt-10 pt-8 border-t border-black/5" data-testid="support-ticket-section">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1 flex items-center gap-2">
        <LifeBuoy className="w-5 h-5 text-brand" /> Submit a support ticket
      </h2>
      <p className="text-brand-slate text-sm mb-5">Need help with something specific? Send us a ticket and our team will follow up.</p>

      {!user ? (
        <div className="glass-panel rounded-3xl p-6 text-center" data-testid="support-login-prompt">
          <p className="text-brand-slate text-sm mb-4">Please sign in to submit a support ticket.</p>
          <Button data-testid="support-login-btn" onClick={login} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            <LogIn className="w-4 h-4 mr-2" /> Sign in with Google
          </Button>
        </div>
      ) : loading ? (
        <div className="text-brand-slate text-sm">Loading…</div>
      ) : (
        <div className="space-y-6">
          {data.tickets.length > 0 && (
            <div className="space-y-2" data-testid="support-my-tickets">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-dim">Your tickets</p>
              {data.tickets.map(t => {
                const s = STATUS[t.status] || STATUS.open;
                return (
                  <div key={t.id} className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-3" data-testid={`support-ticket-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-brand-ink truncate">{t.subject}</p>
                      <p className="text-xs text-brand-slate truncate">{t.message}</p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: s.color + '22', color: s.color }}>
                      {t.status === 'resolved' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="glass-panel rounded-3xl p-6 space-y-4">
            {atLimit ? (
              <p className="text-sm text-brand-slate" data-testid="support-limit-msg">
                You have {data.active} open tickets (the maximum is {data.max_active}). Please wait until one is resolved before submitting another.
              </p>
            ) : (
              <>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Subject</label>
                  <Input data-testid="support-subject" value={subject} onChange={e => setSubject(e.target.value)} maxLength={150} placeholder="Brief summary" className="mt-2 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Message</label>
                  <Textarea data-testid="support-message" value={message} onChange={e => setMessage(e.target.value)} maxLength={3000} placeholder="Tell us what's going on…" className="mt-2 rounded-xl min-h-[120px]" />
                  <p className="mt-1 text-xs text-brand-dim text-right">{message.length}/3000</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-brand-dim">{data.active}/{data.max_active} open tickets</span>
                  <Button data-testid="support-submit-btn" onClick={submit} disabled={sending} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit ticket
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
